import React, { useState, useEffect } from 'react';
import { api, getImageUrl } from '../utils/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { Image, Trash2, Check, Loader2, FolderOpen } from 'lucide-react';
import { useConfirm } from './ConfirmProvider';

export default function ImageLibrary({ onSelectImage, selectedImage }) {
  const confirm = useConfirm();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const data = await api.getArticleImages();
      setImages(data.images || []);
    } catch (error) {
      console.error('Failed to load images:', error);
      toast.error('Failed to load image library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchImages();
    }
  }, [open]);

  const handleSelectImage = (image) => {
    onSelectImage(image.url);
    setOpen(false);
    toast.success('Image selected from library');
  };

  const handleDeleteImage = async (e, image) => {
    e.stopPropagation();
    if (!(await confirm({
      title: 'Delete this image?',
      description: 'It will be removed from your library. This cannot be undone.',
      confirmText: 'Delete image',
    }))) {
      return;
    }
    
    setDeletingImage(image.filename);
    try {
      await api.deleteArticleImage(image.filename);
      setImages(images.filter(img => img.filename !== image.filename));
      toast.success('Image deleted');
    } catch (error) {
      console.error('Failed to delete image:', error);
      toast.error('Failed to delete image');
    } finally {
      setDeletingImage(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          type="button" 
          variant="outline" 
          size="sm"
          className="gap-2"
          data-testid="open-image-library-btn"
        >
          <FolderOpen className="w-4 h-4" />
          Image Library
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="image-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Image Library
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No images uploaded yet</p>
            <p className="text-sm mt-1">Upload images when creating articles to build your library</p>
          </div>
        ) : (
          <ScrollArea className="h-[60vh] pr-4">
            <div className="grid grid-cols-3 gap-3">
              {images.map((image) => (
                <div
                  key={image.filename}
                  onClick={() => handleSelectImage(image)}
                  className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:border-primary ${
                    selectedImage === image.url ? 'border-primary ring-2 ring-primary/30' : 'border-transparent'
                  }`}
                  data-testid={`library-image-${image.filename}`}
                >
                  <div className="aspect-square bg-muted">
                    <img
                      src={getImageUrl(image.url)}
                      alt={image.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="10"%3EError%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                  
                  {/* Overlay with info */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                    <div className="flex justify-end">
                      <button
                        onClick={(e) => handleDeleteImage(e, image)}
                        className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        title="Delete image"
                        disabled={deletingImage === image.filename}
                      >
                        {deletingImage === image.filename ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    <div className="text-white text-xs">
                      <p className="truncate font-medium">{image.filename}</p>
                      <p className="opacity-75">{formatFileSize(image.size)}</p>
                      <p className="opacity-75">{formatDate(image.uploaded_at)}</p>
                    </div>
                  </div>
                  
                  {/* Selected indicator */}
                  {selectedImage === image.url && (
                    <div className="absolute top-2 left-2 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        
        <div className="flex justify-between items-center pt-4 border-t text-sm text-muted-foreground">
          <span>{images.length} image{images.length !== 1 ? 's' : ''} in library</span>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
